import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'
import { toast, openAchievement, i18N } from '@/common/util'
import webStorage from '@/common/webStorage'
import crypto from '@/common/crypto'
import { useAuthStore, useContratosStore } from '@/stores'
import usuariosService from '@/services/usuarios.service'
import { usersService } from '@/services/usuarios.service'
import aliareService from '@/services/aliare/aliare.service'
import autenticacaoService from '@/services/autenticacao.service'
import { authService } from '@/services/autenticacao.service'

import type {
  UsuarioType,
  NewPasswordProps,
  ConfirmEmailType,
  MyProgressType,
  InscricoesUsuario,
  PagedListType,
  PaginationParamsType,
  UsersBatchType,
  UserPartner,
  LoaderType,
  ListUsersBasicType,
  UsersBasicType
} from '@/types/types'

export const useUsuariosStore = defineStore('usuarios', () => {
  /**
   * States
   */
  const list = ref<UsuarioType[]>([])
  const listProgress = ref<MyProgressType[]>([])
  const basicList = ref<ListUsersBasicType[]>([])

  const pagedList = ref<PagedListType<UsuarioType>>({
    list: [],
    totalItemsPage: 50,
    totalItems: 50,
    page: 1,
    totalPages: 1
  })

  const partnerList = ref<UserPartner[]>([])

  const status = reactive<Record<string, LoaderType>>({
    create: 'initial',
    update: 'initial',
    getAll: 'initial',
    getById: 'initial',
    delete: 'initial',
    export: 'initial',
    email: 'initial',
    newPassword: 'initial',
    getProgress: 'initial',
    acceptTermOfUse: 'initial',
    fileImport: 'initial',
    nps: 'initial',
    partner: 'initial',
    basicList: 'initial'
  })

  const nStatus = reactive({
    getUserAccounts: 'initial' as LoaderType,
  })

  // const grupoPermissoesStore = useGrupoDePermissoes()
  const authStore = useAuthStore()

  /**
   * Getters
   */
  const loading = computed(() => Object.values(status).some(el => el === 'loading'))

  const pagedInfo = computed(() => {
    return {
      totalItemsPage: pagedList.value.totalItemsPage,
      totalItems: pagedList.value.totalItems,
      page: pagedList.value.page,
      totalPages: pagedList.value.totalPages
    }
  })

  const hasPartners = computed(() => partnerList.value.length > 1)
  const loadingBasicList = computed(() => status.basicList === 'loading')

  const lazyStatus = ref('initial' as LoaderType)
  const lazyFilter = ref('')

  const lazyBasicList = computed(() => (opt?: any) => {
    if (lazyStatus.value === 'initial' || lazyFilter.value !== JSON.stringify(opt || {})) {
      lazyFilter.value = JSON.stringify(opt || {})
      getBasicList(opt)
    }

    if (lazyStatus.value === 'loading') {
      return []
    }

    return basicList.value?.map((c) => ({ label: c.nomeCompleto, value: c.id })) || []
  })

  /**
   * Actions
   */
  const create = async (dataObj: UsuarioType) => {
    status.create = 'loading'
    try {
      const { data, success } = await usuariosService.create(dataObj)
      if (success) {
        getAll()
        useContratosStore().getUsageInfo()
        status.create = 'success'
      } else {
        status.create = 'error'
      }

      return data
    } catch {
      status.create = 'error'
    }
  }

  const update = async (dataObj: UsuarioType, id: string) => {
    status.update = 'loading'
    try {
      const { data, success } = await usuariosService.update<UsuarioType>(id, dataObj)
      if (success) {
        getAll()
        useContratosStore().getUsageInfo()
        status.update = 'success'
      } else {
        status.update = 'error'
      }

      if (success && data?.conquistas) {
        data.conquistas?.map(c => {
          openAchievement({
            title: 'Parabéns!',
            subtitle: c.regra,
            points: c.pontuacao || 0
          })
        })
      }

      return data
    } catch {
      status.update = 'error'
    }
  }

  const getAll = async (pagination?: PaginationParamsType) => {
    status.getAll = 'loading'
    try {
      const { getActiveTentant, partnerIdPlantar, hasElevatedPrivileges } = authStore
      const result = await usuariosService.getUsersByTenant(
        getActiveTentant,
        hasElevatedPrivileges ? partnerIdPlantar : null,
        pagination
      )

      pagedList.value = {
        totalItems: result.totalItems,
        totalItemsPage: result.pageSize,
        list: result.data,
        page: result.pageNumber,
        totalPages: result.totalPages
      }
      status.getAll = 'success'
    } catch {
      status.getAll = 'error'
    }
  }

  const remove = async (id: string) => {
    status.delete = 'loading'
    try {
      const { success } = await usuariosService.remove(id)
      if (success) {
        getAll()
        useContratosStore().getUsageInfo()
        status.delete = 'success'
      } else {
        status.delete = 'error'
      }

      return success
    } catch {
      status.delete = 'error'
    }
  }

  const getById = async (id: string): Promise<UsuarioType> => {
    status.getById = 'loading'
    try {
      const { data } = await usuariosService.get(id)
      status.getById = 'success'
      return data as UsuarioType
    } catch {
      status.getById = 'error'
    }
  }

  const exportUsuarios = async () => {
    status.export = 'loading'
    try {
      const csvFile = await usuariosService.exportCsv()
      status.export = 'success'
      return csvFile
    } catch {
      status.export = 'error'
    }
  }

  const sendEmailChangePassword = async (email: string, tenantId?: string, recaptchaToken?: string) => {
    status.email = 'loading'

    try {
      const { success } = await autenticacaoService.forgotPassword(email, tenantId, recaptchaToken)
      status.email = 'success'
      return { success }
    } catch (error) {
      status.email = 'error'
      const { success, notifications } = error.response?.data || {}
      return { success, notifications }
    }
  }

  const sendNewPassword = async (data: NewPasswordProps, newPassword?: boolean) => {
    status.newPassword = 'loading'

    try {
      const result = await autenticacaoService.sendNewPassword(data)

      if (result)
        toast.open({
          type: 'success',
          title: newPassword ? i18N('toasts.users.accessValidated') : i18N('toasts.users.resetPassword'),
          message: newPassword ? i18N('toasts.users.welcome') : i18N('toasts.users.updatedPassword')
        })

      status.newPassword = 'success'
      return result
    } catch (e) {
      status.newPassword = 'error'
      return { success: false, data: e?.data }
    }
  }

  const confirmEmail = async (data: ConfirmEmailType) => {
    status.email = 'loading'

    try {
      const response = await aliareService.confirmEmail(data)
      status.email = 'success'
      return response
    } catch {
      status.email = 'error'
    }
  }

  /**
   * Envia multiplas requisições de recuperação de senha
   * @param emailList
   */
  const massRecoverPassword = async (emailList: string[]) => {
    try {
      const { data, success } = await authService.bulkforgotPassword(emailList)

      return {
        success,
        data
      }
    } catch (error) {
      return { success: false, data: error }
    }
  }

  const getProgress = async () => {
    status.getProgress = 'loading'

    try {
      const { data } = await usuariosService.getProgressUser()
      listProgress.value = data

      status.getProgress = 'success'
    } catch {
      status.getProgress = 'error'
    }
  }

  const getSubscriptions = async (userId: string): Promise<InscricoesUsuario[]> => {
    try {
      const { data } = await usuariosService.getSubscriptionsUser(userId)
      return data
    } catch {
      //
    }
  }

  const acceptTermOfUse = async (userId: string) => {
    status.acceptTermOfUse = 'loading'
    try {
      await usuariosService.acceptTermOfUse(userId)
      status.acceptTermOfUse = 'success'
    } catch {
      status.acceptTermOfUse = 'error'
    }
  }

  const validateFileImport = async (file: File): Promise<UsersBatchType> => {
    status.fileImport = 'loading'
    try {
      const { data } = await usuariosService.validateFileImport(file)
      status.fileImport = 'success'
      return data
    } catch (error) {
      status.fileImport = 'error'
    }
  }

  const importUsers = async (fileId: string) => {
    status.fileImport = 'loading'
    try {
      const { data } = await usuariosService.importUsers(fileId)
      status.fileImport = 'success'
      return data
    } catch (error) {
      status.fileImport = 'error'
    }
  }

  const getImportStatus = async (batchId: string) => {
    return await usuariosService.getImportStatus(batchId)
  }

  const getUserInfoNPS = async () => {
    status.nps = 'loading'
    try {
      const key = crypto.base64URLEncode('nps-user-info')
      const userInfo = webStorage.getItem(key)

      if (userInfo?.tenantId) {
        status.nps = 'success'
        return userInfo
      }

      const { data } = await usuariosService.getUserInfoNPS()
      webStorage.setItem(key, data, true)

      status.nps = 'success'

      return data
    } catch (error) {
      status.nps = 'error'
    }
  }

  const updateUserEmail = async (id: string, email: string) => {
    status.email = 'loading'
    try {
      const { success } = await usuariosService.updateUserEmail(id, email)

      if (success) {
        toast.success({ message: i18N('toasts.users.updatedEmail') })
        getAll()
      }

      status.email = 'success'
      return success
    } catch {
      status.email = 'error'
    }
  }

  const clearUserInfoNps = () => webStorage.removeItem(crypto.base64URLEncode('nps-user-info'))

  /**
   * Retorna a lista de contas do usuário
   */
  const getUserAccounts = async () => {
    try {
      nStatus.getUserAccounts = 'loading'
      const { data } = await usuariosService.getUserAccounts()

      partnerList.value = data
      nStatus.getUserAccounts = 'success'

      return data
    } catch {
      nStatus.getUserAccounts = 'error'
    }
  }

  const setDefaultPartner = async (tenantId: string) => {
    try {
      status.partner = 'loading'
      const { success } = await usuariosService.setDefaultPartner(tenantId)

      if (success) {
        toast.success({ message: i18N('toasts.users.defaultAccount') })
        partnerList.value = partnerList.value.map((el) => ({ ...el, isPrincipal: el.tenantId === tenantId }))
        status.partner = 'success'
      }

      return success
    } catch {
      status.partner = 'error'
    }
  }

  const getBasicList = (params?: UsersBasicType) => {
    status.basicList = 'loading'
    lazyStatus.value = 'loading'
    return usersService.getListBasic({ ...params })
      .then(({ data }) => {
        basicList.value = (data || [])
        status.basicList = 'success'
        lazyStatus.value = 'success'
        return basicList.value
      })
      .catch(() => {
        status.basicList = 'error'
        lazyStatus.value = 'error'
      })
  }

  return {
    list,
    pagedList,
    listProgress,
    lazyBasicList,
    eagerBasicList: computed(() => basicList.value),
    status,
    nStatus,
    partnerList,
    basicList,
    // Getters
    loading,
    loadingBasicList,
    pagedInfo,
    hasPartners,
    // Actions
    create,
    update,
    getAll,
    getById,
    remove,
    exportUsuarios,
    sendEmailChangePassword,
    sendNewPassword,
    confirmEmail,
    massRecoverPassword,
    getProgress,
    getSubscriptions,
    acceptTermOfUse,
    validateFileImport,
    importUsers,
    getImportStatus,
    getUserInfoNPS,
    updateUserEmail,
    clearUserInfoNps,
    getUserAccounts,
    setDefaultPartner,
    getBasicList
  }
})

export default useUsuariosStore
